import { LONG_RUN_MAX_SAVING_PERMILLE } from './constants.js';
import type { ElevatorSpec } from './types.js';

/**
 * Elevator hardware.
 *
 * The plan update is blunt about the failure mode here, and it is worth keeping
 * in front of you while editing this file: upgrades must not read as
 * "+8% cable strength". Every tier below is a **named piece of equipment with a
 * number the player can hold in their head** — 0.8 seconds a floor becoming 0.6,
 * four seats becoming six — because the player has to be able to say what
 * changed, why it matters, and what it now lets them do.
 *
 * Four systems, six tiers each (ADR-0033): the motor, the cabin, the doors and
 * the cable. Three tiers each was where this began, when the question was only
 * whether upgrading is felt; the owner widened the garage once it was, to make
 * it somewhere worth coming back to. Tiers 0–2 are what they always were, so a
 * loadout recorded against them still means the same lift.
 */

export type UpgradeSystem = 'motor' | 'cabin' | 'doors' | 'cable';

export interface UpgradeTier {
  /** What the player sees. Equipment, not a stat line. */
  readonly name: string;
  /** The one number that defines it, already in the player's units. */
  readonly headline: string;
  /** What it lets you do that you could not do before. */
  readonly effect: string;
  /** Coins to install. Zero for the tier you start with. */
  readonly cost: number;
  /**
   * The change to the cabin, applied over the level's base specification.
   *
   * On tier 0 this is descriptive only and never applied — it says what the
   * stock lift is so the shop can show a before and after, while the level
   * itself stays in charge of its own baseline.
   */
  readonly spec: Partial<ElevatorSpec>;
}

/** Every tier of every system: one lift's whole catalogue. */
export type LiftParts = Readonly<Record<UpgradeSystem, readonly UpgradeTier[]>>;

/**
 * Step changes, never increments.
 *
 * A motor that shaves 25% off every journey is something you notice on the next
 * level; one that shaves 4% is a number in a menu. The roadmap asks for the
 * former in as many words.
 */
export const UPGRADES: LiftParts = {
  motor: [
    {
      name: 'Standard Motor',
      headline: '0.8s per floor',
      effect: 'The lift you started with.',
      cost: 0,
      spec: { msPerFloor: 800 },
    },
    {
      name: 'Geared Motor',
      headline: '0.6s per floor',
      effect: 'A quarter off every trip: people reach their desks sooner.',
      cost: 60,
      spec: { msPerFloor: 600 },
    },
    {
      name: 'High-Speed Motor',
      headline: '0.45s per floor',
      effect: 'From the lobby to the top floor is now quick enough to be worth it.',
      cost: 140,
      spec: { msPerFloor: 450 },
    },
    {
      name: 'Precision Motor',
      headline: '0.36s per floor',
      effect: 'Every floor is reached sooner, so nobody stands waiting for long.',
      cost: 200,
      spec: { msPerFloor: 360 },
    },
    {
      name: 'Turbo Motor',
      headline: '0.3s per floor',
      effect: 'Fast enough that one wrong floor costs the office little.',
      cost: 280,
      spec: { msPerFloor: 300 },
    },
    {
      name: 'Linear Motor',
      headline: '0.25s per floor',
      effect: 'The whole office feels shorter: every plan has time to spare.',
      cost: 380,
      spec: { msPerFloor: 250 },
    },
  ],
  cabin: [
    {
      name: 'Four-Seat Cabin',
      headline: '4 seats',
      effect: 'The lift you started with.',
      cost: 0,
      spec: { capacity: 4 },
    },
    {
      name: 'Five-Seat Cabin',
      headline: '5 seats',
      effect: 'One more seat: a luggage traveller no longer costs you half the cabin.',
      cost: 70,
      spec: { capacity: 5 },
    },
    {
      name: 'Six-Seat Cabin',
      headline: '6 seats',
      effect: 'A full lobby can leave in one trip instead of two.',
      cost: 160,
      spec: { capacity: 6 },
    },
    {
      name: 'Seven-Seat Cabin',
      headline: '7 seats',
      effect: 'Two lobby groups in one trip, with a seat to spare.',
      cost: 230,
      spec: { capacity: 7 },
    },
    {
      name: 'Eight-Seat Cabin',
      headline: '8 seats',
      effect: 'The morning rush fits in two trips instead of three.',
      cost: 320,
      spec: { capacity: 8 },
    },
    {
      name: 'Nine-Seat Cabin',
      headline: '9 seats',
      effect: 'Almost nobody is left waiting on a floor.',
      cost: 430,
      spec: { capacity: 9 },
    },
  ],
  doors: [
    {
      name: 'Manual Doors',
      headline: '0.6s to open and close · 0.22s per person',
      effect: 'The lift you started with.',
      cost: 0,
      spec: { doorOpenMs: 300, doorCloseMs: 300, transferMs: 220 },
    },
    {
      name: 'Powered Doors',
      headline: '0.44s to open and close · 0.16s per person',
      effect: 'A full lobby gets in quicker.',
      cost: 55,
      spec: { doorOpenMs: 220, doorCloseMs: 220, transferMs: 160 },
    },
    {
      name: 'Express Doors',
      headline: '0.3s to open and close · 0.11s per person',
      effect: 'Stopping for one more colleague is cheap enough to be worth it.',
      cost: 130,
      spec: { doorOpenMs: 150, doorCloseMs: 150, transferMs: 110 },
    },
    {
      name: 'Rapid Doors',
      headline: '0.24s to open and close · 0.09s per person',
      effect: 'Picking someone up on the way costs almost nothing.',
      cost: 190,
      spec: { doorOpenMs: 120, doorCloseMs: 120, transferMs: 90 },
    },
    {
      name: 'Turbo Doors',
      headline: '0.2s to open and close · 0.07s per person',
      effect: 'A crowded floor is in the lift before you have decided.',
      cost: 260,
      spec: { doorOpenMs: 100, doorCloseMs: 100, transferMs: 75 },
    },
    {
      name: 'Instant Doors',
      headline: '0.16s to open and close · 0.06s per person',
      effect: 'People step in and out so fast the doors never hold anyone up.',
      cost: 350,
      spec: { doorOpenMs: 80, doorCloseMs: 80, transferMs: 60 },
    },
  ],
  // The cable (ADR-0033): a share off the motor's time a floor, once a run is under way.
  cable: [
    {
      name: 'Twin Steel Ropes',
      headline: '2 ropes · the motor’s speed all the way',
      effect: 'The ropes you started with: the motor sets the pace all the way.',
      cost: 0,
      spec: { longRunSavingPermille: 0 },
    },
    {
      name: 'Steel Wire Rope',
      headline: '6 floors in the time of 5 · after the first floor',
      effect: 'Past the first floor, every floor goes by a little faster: long trips shrink.',
      cost: 40,
      spec: { longRunSavingPermille: 167 },
    },
    {
      name: 'Compacted Strand Rope',
      headline: '5 floors in the time of 4 · after the first floor',
      effect: 'Long trips get shorter again: crossing half the building is quick.',
      cost: 90,
      spec: { longRunSavingPermille: 200 },
    },
    {
      name: 'Aramid Rope',
      headline: '4 floors in the time of 3 · after the first floor',
      effect: 'Light and strong: after the first floor, the car goes much faster.',
      cost: 140,
      spec: { longRunSavingPermille: 250 },
    },
    {
      name: 'Carbon Fibre Rope',
      headline: '3 floors in the time of 2 · after the first floor',
      effect: 'Top-to-bottom trips become something you plan for, not something you avoid.',
      cost: 200,
      spec: { longRunSavingPermille: 333 },
    },
    {
      name: 'Carbon Belt',
      headline: '5 floors in the time of 3 · after the first floor',
      effect: 'A belt, not a rope: the longest trip in the building is the cheapest per floor.',
      cost: 260,
      spec: { longRunSavingPermille: 400 },
    },
  ],
};

export const UPGRADE_SYSTEMS = Object.keys(UPGRADES) as UpgradeSystem[];

/**
 * A building's own lift (ADR-0027): its parts, from the stock car it starts with
 * to the best it can be fitted with.
 *
 * Every building has one, bought separately — moving to a new building means a
 * new lift, one class up from the last building's stock, and the old one stays
 * where it was left. The catalogue is data, declared by the building
 * (`packages/content/buildings/*.json`); a building that declares none uses
 * `STANDARD_LIFT`, which is `UPGRADES` and the lift every run before ADR-0027
 * was played on.
 */
export interface Lift {
  /** The building's id, or `standard`. Saves, replays and the garage key on it. */
  readonly id: string;
  readonly parts: LiftParts;
}

export const STANDARD_LIFT: Lift = { id: 'standard', parts: UPGRADES };

/**
 * Which tier of each system is installed. Index into the lift's parts.
 *
 * The cable may be missing (ADR-0033): every loadout recorded before it
 * existed has none, and none is the stock rope.
 */
export type Loadout = Readonly<Record<Exclude<UpgradeSystem, 'cable'>, number>> & {
  readonly cable?: number;
};

/** Everything at tier 0: the stock car, which every level's baseline already is. */
export const BASE_LOADOUT: Loadout = { motor: 0, cabin: 0, doors: 0, cable: 0 };

export function tierOf(
  system: UpgradeSystem,
  loadout: Loadout,
  parts: LiftParts = UPGRADES,
): UpgradeTier {
  const tiers = parts[system];
  const index = Math.max(0, Math.min(loadout[system] ?? 0, tiers.length - 1));
  return tiers[index] as UpgradeTier;
}

/**
 * The stock car of a lift, as a cabin specification: every system's tier 0.
 *
 * Content folds this into the baseline of every level in the lift's building
 * when it parses the level, so the level still owns its baseline — it inherits
 * it from where it stands — and `applyLoadout` still never applies tier 0.
 */
export function stockSpec(parts: LiftParts): Partial<ElevatorSpec> {
  return Object.assign({}, ...UPGRADE_SYSTEMS.map((system) => parts[system][0]?.spec ?? {}));
}

/**
 * Fold the installed hardware into a level's cabin.
 *
 * The level supplies the baseline; the loadout replaces the parts the player has
 * changed. A level therefore never has to know what the player owns, which is
 * what keeps content and progression from tangling.
 */
export function applyLoadout(
  spec: ElevatorSpec,
  loadout: Loadout = BASE_LOADOUT,
  parts: LiftParts = UPGRADES,
): ElevatorSpec {
  let result = spec;
  for (const system of UPGRADE_SYSTEMS) {
    // Tier 0 only *describes* the stock lift, for the shop to display. Applying
    // it would overwrite the level's own cabin with the catalogue's idea of a
    // default, and a level would quietly lose the ability to specify anything.
    if ((loadout[system] ?? 0) === 0) continue;
    result = { ...result, ...tierOf(system, loadout, parts).spec };
  }
  return result;
}

/** Cost of the next tier of a system, or null when it is fully upgraded. */
export function nextTier(
  system: UpgradeSystem,
  loadout: Loadout,
  parts: LiftParts = UPGRADES,
): { index: number; tier: UpgradeTier } | null {
  const index = (loadout[system] ?? 0) + 1;
  const tier = parts[system][index];
  return tier ? { index, tier } : null;
}

export function isBaseLoadout(loadout: Loadout): boolean {
  return UPGRADE_SYSTEMS.every((system) => (loadout[system] ?? 0) === 0);
}

/** What a loadout cost to fit, tier by tier, in a lift's prices. */
export function loadoutCost(loadout: Loadout, parts: LiftParts = UPGRADES): number {
  return UPGRADE_SYSTEMS.reduce((sum, system) => {
    const tiers = parts[system].slice(1, (loadout[system] ?? 0) + 1);
    return sum + tiers.reduce((s, tier) => s + tier.cost, 0);
  }, 0);
}

/** The cabin numbers a tier moves, and which way is better for each. */
const BETTER: Readonly<Partial<Record<keyof ElevatorSpec, 'higher' | 'lower'>>> = {
  capacity: 'higher',
  msPerFloor: 'lower',
  doorOpenMs: 'lower',
  doorCloseMs: 'lower',
  transferMs: 'lower',
  longRunSavingPermille: 'higher',
};

/**
 * Everything wrong with a lift's catalogue, as sentences; empty when it is sound.
 *
 * The rules every lift answers to, whoever wrote it: three systems; a free
 * stock tier and paid ones after it, dearer as they get better; a named part, a
 * number in the player's units and a sentence about what it lets them do —
 * never a percentage; each tier better than the last, on the numbers it
 * declares, by at least a tenth, so it is a thing you feel and not a number in
 * a menu; and only numbers a lift can have. Content runs this on every building
 * it loads, so a bad catalogue never reaches a player.
 */
export function liftProblems(parts: LiftParts): string[] {
  const problems: string[] = [];
  for (const system of UPGRADE_SYSTEMS) {
    const tiers = parts[system];
    if (!tiers || tiers.length < 2) {
      problems.push(`${system}: needs a stock tier and at least one to buy`);
      continue;
    }
    if (tiers[0]?.cost !== 0) problems.push(`${system}: the stock tier must be free`);
    let spec: Partial<ElevatorSpec> = {};
    tiers.forEach((tier, index) => {
      const label = `${system} tier ${index} (${tier.name})`;
      problems.push(...wordingProblems(tier, label));
      if (index > 0)
        problems.push(...stepProblems(tier, tiers[index - 1] as UpgradeTier, spec, label));
      if ((tier.spec.longRunSavingPermille ?? 0) > LONG_RUN_MAX_SAVING_PERMILLE) {
        problems.push(`${label}: a rope cannot save more than ${LONG_RUN_MAX_SAVING_PERMILLE}‰`);
      }
      spec = { ...spec, ...tier.spec };
    });
  }
  return problems;
}

/** A named part, its number, a sentence — and nothing a lift does not have. */
function wordingProblems(tier: UpgradeTier, label: string): string[] {
  const problems: string[] = [];
  if (/%/.test(`${tier.name} ${tier.headline} ${tier.effect}`)) {
    problems.push(`${label}: a percentage — name the part and its number instead`);
  }
  if (!/\d/.test(tier.headline)) problems.push(`${label}: the headline needs its number`);
  if (tier.name.trim() === '' || tier.effect.trim() === '') {
    problems.push(`${label}: needs a name and a sentence about what it lets you do`);
  }
  for (const key of Object.keys(tier.spec) as (keyof ElevatorSpec)[]) {
    if (!BETTER[key]) problems.push(`${label}: "${key}" is not a number a part can change`);
  }
  return problems;
}

/** Dearer than the tier below, better on every number it declares, and by enough to feel. */
function stepProblems(
  tier: UpgradeTier,
  previous: UpgradeTier,
  below: Partial<ElevatorSpec>,
  label: string,
): string[] {
  const problems: string[] = [];
  if (tier.cost <= previous.cost) problems.push(`${label}: must cost more than the tier below`);
  let felt = false;
  for (const key of Object.keys(tier.spec) as (keyof ElevatorSpec)[]) {
    const before = below[key];
    const after = tier.spec[key];
    const way = BETTER[key];
    if (typeof before !== 'number' || typeof after !== 'number' || !way) {
      problems.push(`${label}: "${key}" has no stock value to improve on`);
      continue;
    }
    const better = way === 'higher' ? after > before : after < before;
    if (!better) problems.push(`${label}: "${key}" is no better than the tier below`);
    // A tenth, or one more seat: a seat is always felt, however big the car.
    if (Math.abs(after - before) / Math.abs(before || 1) >= 0.1) felt = true;
    if (key === 'capacity' && after - before >= 1) felt = true;
  }
  if (!felt) problems.push(`${label}: moves nothing by at least a tenth`);
  return problems;
}
